/**
 * Signup Wizard - Main Entry Point
 * Composes all signup modules into an Alpine.js component
 */

import {
    getDayOptions, getYearOptions, getLocaleDateFormat, calculateAge, formatBirthdayLong,
} from './utils/dateHelpers.js';
import { loadState, saveState, clearState } from './state/localStorage.js';
import { initializeFormData } from './state/formData.js';
import {
    isStepValid, getStepError, getFirstInvalidStep, markStepTouched, validateStep, stepErrorOffersSignIn,
    clearServerError,
} from './validation/stepValidation.js';
import { focusCurrentInput, isCityStep, getCityInputId, BIRTHDAY_STEP, SEX_STEP } from './navigation/stepNavigation.js';
import { requestLocation, requestIpLocation } from './geolocation/index.js';
import { initializeCityAutocomplete, cleanupCityAutocomplete } from './autocomplete/cityAutocomplete.js';
import { submitSignup } from './submission/apiSubmission.js';
import { mountTurnstile, resetTurnstile, whenTurnstileSettles } from './submission/turnstile.js';
import { checkAvailability, applyKnownRefusal, isKnownAvailable, recordSubmitRefusal } from './validation/availability.js';
import { getApiFieldForStep, getStepForApiField, getStepForField, getFieldForStep } from './steps.js';
import { trackStep, trackStepBack, trackError } from './analytics/signupAnalytics.js';

export function signupWizard() {
    // Load saved state before Alpine initializes
    const savedState = loadState();
    const initialFormData = initializeFormData(savedState || {});

    return {
        // Spread initial form data
        ...initialFormData,

        // Autocomplete instances (for cleanup)
        _autocompleteInstances: {},

        // Step indices the confirm sheet targets, so the template carries no
        // step numbers of its own.
        birthdayStep: BIRTHDAY_STEP,
        sexStep: SEX_STEP,

        // init() runs twice: Alpine calls it, then x-init calls it with the server config.
        _entryStepTracked: false,

        // Turnstile state on the last step, see submission/turnstile.js.
        turnstileState: 'off',
        // Set when Create Account ran while Cloudflare showed its checkbox.
        turnstileNudge: false,

        // Computed properties
        get progress() {
            return (this.currentStep / this.totalSteps) * 100;
        },

        get canProceed() {
            return isStepValid(this.currentStep, this);
        },

        get isLastStep() {
            return this.currentStep === this.totalSteps;
        },

        get isFirstStep() {
            return this.currentStep === 1;
        },

        get currentStepError() {
            // Make error reactive by accessing errors and touched directly
            return getStepError(this.currentStep, this);
        },

        get currentStepErrorOffersSignIn() {
            return stepErrorOffersSignIn(this.currentStep, this);
        },

        get usernameAvailable() {
            return !this.currentStepError && isKnownAvailable(this, 'username');
        },

        get dayOptions() {
            return getDayOptions();
        },

        get yearOptions() {
            return getYearOptions();
        },

        get dateFormat() {
            return getLocaleDateFormat();
        },

        // Initialization
        init(config = {}) {
            // Apply server config if provided
            if (Object.keys(config).length > 0) {
                const formData = initializeFormData(config);
                Object.assign(this, formData);
            }

            this.trackEntryStep();

            // Set up auto-save watchers
            this.setupAutoSave();

            this.$watch('currentStep', () => this.mountTurnstileOnLastStep());

            // Initialize UI after Alpine is ready
            this.$nextTick(() => {
                this.markAlpineReady();

                // Show content after initialization is complete
                this.showContent = true;

                // Wait for transition to complete before enabling validation
                setTimeout(() => {
                    this.isInitializing = false;
                    this.focusCurrentInput();
                }, 350);

                this.mountTurnstileOnLastStep();

                // Initialize city autocomplete if on city step at page load
                if (isCityStep(this.currentStep)) {
                    this.initLocationAutocomplete();
                    // Don't request location here - wait for user navigation to maintain gesture chain
                }
            });
        },

        // Auto-save setup
        setupAutoSave() {
            const fieldsToWatch = [
                'name', 'email', 'day', 'month', 'year', 'sex',
                'username', 'password', 'cityId', 'cityName', 'cityCountryIso',
                'homeCityId', 'homeCityName', 'homeCityCountryIso', 'currentStep'
            ];

            fieldsToWatch.forEach(field => {
                this.$watch(field, () => saveState(this));
            });

            // Editing either locked value withdraws the confirmation, so the
            // sheet shows what the user actually chose last.
            ['sex', 'day', 'month', 'year'].forEach(field => {
                this.$watch(field, () => {
                    this.identityConfirmed = false;
                });
            });
        },

        // Analytics

        // The step the wizard opens on. A restored session opens past step 1.
        trackEntryStep() {
            if (this._entryStepTracked) {
                return;
            }

            this._entryStepTracked = true;
            trackStep(this.currentStep);
        },

        trackStepError(step = this.currentStep) {
            const result = validateStep(step, this);

            if (result.valid) {
                return;
            }

            trackError(step, getApiFieldForStep(step), result.reason);
        },

        markAlpineReady() {
            const form = document.getElementById('signupForm');
            if (form && form.closest('[x-data]')) {
                form.closest('[x-data]').classList.add('alpine-ready');
            }
        },

        // State management
        loadState,
        clearState,

        // Validation
        isStepValid(step) {
            return isStepValid(step, this);
        },

        getStepError() {
            return getStepError(this.currentStep, this);
        },

        clearServerError(field) {
            clearServerError(this, field);
        },

        getFirstInvalidStep() {
            return getFirstInvalidStep(this, this.totalSteps);
        },

        markTouched() {
            markStepTouched(this.currentStep, this);
        },

        checkEmail() {
            return checkAvailability(this, 'email', (email) => window.ApiClient.checkEmailAvailability(email));
        },

        checkUsername() {
            return checkAvailability(this, 'username', (username) => window.ApiClient.checkUsernameAvailability(username));
        },

        /**
         * A forward move off a step with an early check. A refusal already in hand
         * keeps the user on the step. On the email step an answer still out moves
         * the user back if it refuses, because waiting for it would lose the tap
         * that opens the mobile keyboard. The username step is last, so its
         * forward move is the submit, which judges the username itself.
         *
         * @returns {boolean} true when the move must not happen
         */
        holdOnCheckedStep() {
            const field = getFieldForStep(this.currentStep);
            if (!(field in this.availabilityChecks)) {
                return false;
            }

            if (applyKnownRefusal(this, field)) {
                return true;
            }

            if (field !== 'email') {
                return false;
            }

            const emailStep = this.currentStep;
            this.checkEmail().then((refused) => {
                // The server sent the user back, so this move emits no step event.
                if (refused && this.currentStep !== emailStep) {
                    this.moveToStep(emailStep);
                }
            });

            return false;
        },

        // Navigation
        async next() {
            if (this.isSubmitting) return;

            this.markTouched();

            if (!this.canProceed) {
                this.trackStepError();
                return;
            }

            if (this.holdOnCheckedStep()) {
                return;
            }

            // Submit if on last step
            if (this.isLastStep) {
                await this.submitSignup();
                return;
            }

            const nextStep = this.currentStep + 1;

            if (this.requiresIdentityConfirm(nextStep)) {
                this.askIdentityConfirm(() => this.next());
                return;
            }

            await this.moveToStep(nextStep);
            trackStep(nextStep);
        },

        async back() {
            if (this.isFirstStep) return;

            const fromStep = this.currentStep;
            const prevStep = fromStep - 1;

            await this.moveToStep(prevStep);
            trackStepBack(fromStep, prevStep);
        },

        // A move the user asked for: a step dot, or Edit on the confirm sheet.
        async goToStep(step) {
            if (step < 1 || step > this.totalSteps) return;
            // A blocked dot shows no error. The event counts the stopped move, not the display.
            if (step > this.currentStep && !this.canProceed) {
                this.trackStepError();
                return;
            }
            if (step > this.currentStep && this.holdOnCheckedStep()) {
                return;
            }

            if (this.requiresIdentityConfirm(step)) {
                this.askIdentityConfirm(() => this.goToStep(step));
                return;
            }

            const fromStep = this.currentStep;

            await this.moveToStep(step);

            if (step > fromStep) {
                trackStep(step);
            } else if (step < fromStep) {
                trackStepBack(fromStep, step);
            }
        },

        async moveToStep(step) {
            // Request location if going to city step
            this.enterCityStepGeo(step);

            // Change step immediately to maintain user gesture chain for mobile keyboard
            this.isTransitioning = true;
            this.currentStep = step;

            await this.$nextTick();

            this.isTransitioning = false;
            this.afterStepChange();
        },

        afterStepChange() {
            // Focus input immediately for mobile keyboard
            this.focusCurrentInput();

            // Initialize city autocomplete if on city step
            if (isCityStep(this.currentStep)) {
                this.initLocationAutocomplete();
            }
        },

        focusCurrentInput() {
            focusCurrentInput(this.currentStep);
        },

        // Sex selection
        selectSex(value) {
            this.sex = value;
            this.touched.sex = true;
        },

        // Arrow keys move the selection inside the sex radio group, which is
        // what the radio role promises. Both options stay tab stops. A roving
        // tabindex would make the two option rows asymmetric.
        moveSex(event) {
            const order = ['male', 'female'];
            const group = event.target.closest('[role="radiogroup"]');

            this.selectSex(order[(order.indexOf(this.sex) + 1) % order.length]);

            this.$nextTick(() => {
                const option = group && group.querySelector('[role="radio"][aria-checked="true"]');
                if (option) {
                    option.focus();
                }
            });
        },

        // Confirm sheet for the locked identity fields

        // Every forward move past the gender step (next, dots, submit) asks once. A move back never does.
        requiresIdentityConfirm(targetStep) {
            return targetStep > SEX_STEP && targetStep > this.currentStep && !this.identityConfirmed;
        },

        /**
         * Open the sheet and keep what it interrupted, to run after the user
         * confirms. The sheet can only confirm values that exist, so an
         * unfinished birthday or gender step takes the user to that step first.
         *
         * @param {Function} resume - the navigation or submit the sheet stopped
         */
        askIdentityConfirm(resume) {
            const unfinished = [BIRTHDAY_STEP, SEX_STEP].find((step) => !this.isStepValid(step));

            if (unfinished !== undefined) {
                markStepTouched(unfinished, this);
                // The unfinished step stopped the move. The wizard chose the
                // destination, so the move itself emits no step event.
                this.trackStepError(unfinished);
                this.moveToStep(unfinished);
                return;
            }

            this.identityConfirmResume = resume;
            this.identityConfirmOpen = true;
        },

        get identityBirthdayText() {
            return formatBirthdayLong(this.day, this.month, this.year);
        },

        /**
         * The age the entered birthday gives. It is shown because a mistyped
         * year reads as an ordinary date but an obviously wrong age.
         *
         * @param {string} template - localized copy carrying the %age% token
         */
        identityAgeText(template) {
            const age = calculateAge(this.day, this.month, this.year);

            return age === null ? '' : template.replace('%age%', String(age));
        },

        confirmIdentity() {
            const resume = this.identityConfirmResume || (() => this.next());

            this.identityConfirmed = true;
            this.identityConfirmOpen = false;
            this.identityConfirmResume = null;

            return resume();
        },

        /** Close the sheet and put the user back on the step that owns the value. */
        editIdentity(step) {
            this.identityConfirmOpen = false;
            this.identityConfirmResume = null;

            if (step !== this.currentStep) {
                this.goToStep(step);
            }
        },

        // Geolocation

        // Entry point when navigating onto a city step. The one-time
        // pre-permission explainer is shown before the OS prompt the first
        // time, and its "Allow" button is what actually triggers the prompt
        // (preserving the user-gesture chain the browser requires). Once the
        // explainer has been answered, later visits go straight to the
        // existing request/dropdown path.
        enterCityStepGeo(step) {
            if (!isCityStep(step)) return;

            if (this.locationRequested) {
                this.requestLocationAndShowDropdown();
                return;
            }

            if (!this.geoExplainerShown) {
                this.geoExplainerOpen = true;
                return;
            }

            this.requestLocationAndShowDropdown();
        },

        // Explainer "Allow": triggers the OS prompt synchronously inside this
        // click gesture, which is required for the browser to show it.
        allowGeo() {
            this.geoExplainerShown = true;
            this.geoExplainerOpen = false;
            saveState(this);
            this.requestLocationAndShowDropdown();
        },

        // Explainer "Skip" / dismiss: record the refusal without triggering the
        // OS prompt, then still fill the city dropdown from IP so the nearby
        // list works for users who declined.
        skipGeo() {
            // Only an open explainer represents a real decline. Guard against
            // skipGeo running while the sheet is closed so a decline is always
            // tied to an explicit choice on the open sheet.
            if (!this.geoExplainerOpen) {
                return;
            }
            if (this.locationRequested) {
                this.geoExplainerOpen = false;
                return;
            }
            this.geoExplainerShown = true;
            this.geoExplainerOpen = false;
            this.locationRequested = true;
            this.locationReady = true;
            this.geoObservation = {
                permission_status: 'prompt_dismissed',
                lat: null,
                lon: null,
                accuracy: null
            };
            saveState(this);
            this.fillDropdownFromIp();
        },

        async requestLocationAndShowDropdown() {
            // Skip if already completed
            if (this.locationRequested && this.locationReady) {
                if (isCityStep(this.currentStep) && this.shouldShowDropdown()) {
                    this.showCityDropdown();
                }
                return;
            }

            // Skip if already requesting
            if (this.locationRequested) {
                return;
            }

            this.locationRequested = true;

            // Persist the browser-permission outcome as soon as it is known,
            // before the (possibly slower) IP fallback resolves, so a fast
            // signup completion never posts a null observation. The callback
            // is the single writer of geoObservation here.
            const { coords } = await requestLocation((obs) => {
                this.geoObservation = obs;
                saveState(this);
            });
            if (coords) {
                this.userLat = coords.lat;
                this.userLon = coords.lon;
            }
            this.locationReady = true;
            saveState(this);

            // Show dropdown if on city step
            if (isCityStep(this.currentStep) && this.shouldShowDropdown()) {
                this.showCityDropdown();
            }
        },

        // IP-only coordinates for the city dropdown. Used after the explainer is
        // skipped, so no browser prompt fires.
        async fillDropdownFromIp() {
            const coords = await requestIpLocation();
            if (coords) {
                this.userLat = coords.lat;
                this.userLon = coords.lon;
                saveState(this);
            }
            if (isCityStep(this.currentStep) && this.shouldShowDropdown()) {
                this.showCityDropdown();
            }
        },

        shouldShowDropdown() {
            return (this.currentStep === 5 && !this.cityId) ||
                   (this.currentStep === 6 && !this.homeCityId);
        },

        showCityDropdown() {
            const inputId = getCityInputId(this.currentStep);
            const input = document.getElementById(inputId);
            if (input) {
                setTimeout(() => input.focus(), 100);
            }
        },

        // City autocomplete
        initLocationAutocomplete() {
            this.cleanupLocationAutocomplete();

            let $field;
            let initialCountryIso = '';
            if (this.currentStep === 5) {
                $field = $('input[name="_userLocation"]');
                initialCountryIso = this.cityCountryIso;
            } else if (this.currentStep === 6) {
                $field = $('input[name="_userOrigin"]');
                initialCountryIso = this.homeCityCountryIso;
            } else {
                return;
            }

            if (!$field.length) return;

            const isHomeCity = this.currentStep === 6;
            const instance = initializeCityAutocomplete($field, {
                getCoordinates: () => ({ lat: this.userLat, lon: this.userLon }),
                onCitySelect: (cityId, cityName, countryIso) => {
                    if (isHomeCity) {
                        this.homeCityId = cityId;
                        this.homeCityName = cityName;
                        this.homeCityCountryIso = countryIso || '';
                    } else {
                        this.cityId = cityId;
                        this.cityName = cityName;
                        this.cityCountryIso = countryIso || '';
                    }
                },
                onCityClear: () => {
                    if (isHomeCity) {
                        this.homeCityId = '';
                        this.homeCityName = '';
                        this.homeCityCountryIso = '';
                    } else {
                        this.cityId = '';
                        this.cityName = '';
                        this.cityCountryIso = '';
                    }
                },
                isHomeCity,
                initialCountryIso
            });

            if (instance) {
                this._autocompleteInstances[this.currentStep] = instance;
            }
        },

        cleanupLocationAutocomplete() {
            $('input[name="_userLocation"], input[name="_userOrigin"]').each(function() {
                cleanupCityAutocomplete($(this));
            });
            $(document).off('.nearby');
            this._autocompleteInstances = {};
        },

        // Turnstile

        mountTurnstileOnLastStep() {
            if (!this.isLastStep) {
                return;
            }

            // After x-show paints the step, so the widget renders into a visible node.
            this.$nextTick(() => mountTurnstile((state) => {
                this.turnstileState = state;
                if (state !== 'interactive') {
                    this.turnstileNudge = false;
                }
            }));
        },

        retryTurnstile() {
            resetTurnstile();
            // The retry button hides with the error line, so focus moves to the check's label.
            this.$nextTick(() => document.getElementById('signupTurnstileLabel')?.focus());
        },

        // Form submission
        async submitSignup() {
            // Guard against concurrent invocations. The submit button
            // is `:disabled="isSubmitting"`, but the form's
            // `@submit.prevent` can still fire from an Enter key on
            // the username input, and a server validation error clears
            // `isSubmitting` so a quick resubmit can race the prior
            // in-flight call.
            if (this.isSubmitting) return;
            this.isSubmitting = true;
            this.errors = {};

            // Only validate current step - let API validate everything
            if (!this.isStepValid(this.currentStep)) {
                this.markTouched();
                this.trackStepError();
                this.isSubmitting = false;
                return;
            }

            if (this.holdOnCheckedStep()) {
                this.isSubmitting = false;
                return;
            }

            // A submit leaves the last step, so it also passes the gender step.
            if (this.requiresIdentityConfirm(this.totalSteps + 1)) {
                this.isSubmitting = false;
                this.askIdentityConfirm(() => this.submitSignup());
                return;
            }

            // A check that runs by itself keeps the button in its wait
            // state. TIMEOUT_MS in turnstile.js bounds the wait.
            if (this.turnstileState === 'loading') {
                await whenTurnstileSettles();
            }

            // A blur check can refuse the username during the wait.
            if (this.holdOnCheckedStep()) {
                this.isSubmitting = false;
                return;
            }

            // A checkbox needs the user, so the first click names it. The
            // backend accepts a submit without a token, so the next click
            // submits without one.
            if (this.turnstileState === 'interactive' && !this.turnstileNudge) {
                this.turnstileNudge = true;
                this.isSubmitting = false;
                return;
            }

            // Submit to API
            const result = await submitSignup(this);

            if (result.success) {
                // Clear saved state
                clearState();

                // Redirect to email verification
                window.location.href = '/app/getting-started/email';
            } else {
                this.isSubmitting = false;

                if (result.validationErrors) {
                    // Find which steps have errors
                    const errorFields = Object.keys(result.validationErrors);
                    let targetStep = null;

                    // First check if current step has an error - if so, stay here
                    for (const field of errorFields) {
                        const step = getStepForApiField(field);
                        if (step === this.currentStep) {
                            targetStep = this.currentStep;
                            break;
                        }
                    }

                    // Otherwise go to first step with error
                    if (targetStep === null) {
                        let firstErrorStep = this.totalSteps;
                        for (const field of errorFields) {
                            const step = getStepForApiField(field);
                            if (step && step < firstErrorStep) {
                                firstErrorStep = step;
                            }
                        }
                        targetStep = firstErrorStep;
                    }

                    // Set errors and mark step as touched
                    this.errors = result.validationErrors;
                    // The submit's verdict replaces an earlier username check. An email
                    // refusal can be transient (the DNS rule), so it is not recorded as a check answer.
                    const usernameError = result.validationErrors[getApiFieldForStep(getStepForField('username'))];
                    if (usernameError) {
                        recordSubmitRefusal(this, 'username', usernameError, result.sent?.username);
                    }
                    markStepTouched(targetStep, this);

                    // The server sent the user back, so this move emits no step event.
                    if (targetStep !== this.currentStep) {
                        this.moveToStep(targetStep);
                    }
                } else {
                    this.errors = {
                        general: { msg: result.error }
                    };
                }

                // A blur check that refused during the submit shows now.
                Object.keys(this.availabilityChecks).forEach((field) => applyKnownRefusal(this, field));
            }
        },

        // Cleanup
        destroy() {
            this.cleanupLocationAutocomplete();
            this.userLat = null;
            this.userLon = null;
        }
    };
}
